import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import FileExplorer from './FileExplorer';
import EditorTabs from './EditorTabs';
import { FileNode } from '../../services/applications/types';

interface VSCodeEditorProps {
  files: FileNode[];
  onFilesChange: (files: FileNode[]) => void;
  height?: string;
  theme?: 'vs-dark' | 'light';
}

const VSCodeEditor: React.FC<VSCodeEditorProps> = ({
  files,
  onFilesChange,
  height = '600px',
  theme = 'vs-dark'
}) => {
  const [openFiles, setOpenFiles] = useState<FileNode[]>([]);
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [fileContents, setFileContents] = useState<Record<string, string>>({});

  // Initialize with main.py if no files exist
  useEffect(() => {
    if (files.length === 0) {
      const defaultFiles: FileNode[] = [
        {
          id: '1',
          name: 'main.py',
          type: 'file',
          path: '/main.py',
          language: 'python',
          content: `def main(context):
    """
    Main function that will be executed.
    
    Args:
        context (dict): Execution context with parameters
        
    Returns:
        dict: Result of the function execution
    """
    # Your code here
    result = {
        "status": "success",
        "message": "Function executed successfully",
        "data": {}
    }
    
    return result
`
        },
        {
          id: '2',
          name: 'requirements.txt',
          type: 'file',
          path: '/requirements.txt',
          language: 'text',
          content: '# Add your Python dependencies here\n'
        },
        {
          id: '3',
          name: 'config.json',
          type: 'file',
          path: '/config.json',
          language: 'json',
          content: '{\n  "version": "1.0.0",\n  "description": "Function configuration"\n}\n'
        }
      ];
      onFilesChange(defaultFiles);
    }
  }, []);

  // Update file contents when files prop changes
  useEffect(() => {
    const contents: Record<string, string> = {};
    const flattenFiles = (nodes: FileNode[]) => {
      nodes.forEach(node => {
        if (node.type === 'file' && node.content) {
          contents[node.path] = node.content;
        }
        if (node.children) {
          flattenFiles(node.children);
        }
      });
    };
    flattenFiles(files);
    setFileContents(contents);

    // Open main.py by default
    const mainFile = findFileByPath(files, '/main.py');
    if (mainFile && openFiles.length === 0) {
      setOpenFiles([mainFile]);
      setActiveFile(mainFile.path);
    }
  }, [files]);

  const findFileByPath = (nodes: FileNode[], path: string): FileNode | null => {
    for (const node of nodes) {
      if (node.path === path) return node;
      if (node.children) {
        const found = findFileByPath(node.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  const updateFileContent = (path: string, content: string) => {
    const newContents = { ...fileContents, [path]: content };
    setFileContents(newContents);

    // Update files tree
    const updateNode = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === path) {
          return { ...node, content };
        }
        if (node.children) {
          return { ...node, children: updateNode(node.children) };
        }
        return node;
      });
    };

    onFilesChange(updateNode(files));
  };

  const handleFileSelect = (file: FileNode) => {
    if (file.type !== 'file') return;

    if (!openFiles.find(f => f.path === file.path)) {
      setOpenFiles([...openFiles, file]);
    }
    setActiveFile(file.path);
  };

  const handleTabClose = (filePath: string) => {
    const newOpenFiles = openFiles.filter(f => f.path !== filePath);
    setOpenFiles(newOpenFiles);
    
    if (activeFile === filePath && newOpenFiles.length > 0) {
      setActiveFile(newOpenFiles[newOpenFiles.length - 1].path);
    } else if (newOpenFiles.length === 0) {
      setActiveFile(null);
    }
  };

  const handleFileCreate = (parentPath: string, type: 'file' | 'folder') => {
    const newName = window.prompt(`Enter ${type} name:`);
    if (!newName) return;

    const newNode: FileNode = {
      id: Date.now().toString(),
      name: newName,
      type,
      path: parentPath === '/' ? `/${newName}` : `${parentPath}/${newName}`,
      content: type === 'file' ? '' : undefined,
      children: type === 'folder' ? [] : undefined,
      language: type === 'file' ? getLanguageFromFileName(newName) : undefined
    };

    const addNode = (nodes: FileNode[]): FileNode[] => {
      if (parentPath === '/') {
        return [...nodes, newNode];
      }
      
      return nodes.map(node => {
        if (node.path === parentPath && node.type === 'folder') {
          return {
            ...node,
            children: [...(node.children || []), newNode]
          };
        }
        if (node.children) {
          return { ...node, children: addNode(node.children) };
        }
        return node;
      });
    };

    onFilesChange(addNode(files));

    // Open the new file if it's a file
    if (type === 'file') {
      handleFileSelect(newNode);
    }
  };

  const handleFileDelete = (filePath: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    const removeNode = (nodes: FileNode[]): FileNode[] => {
      return nodes
        .filter(node => node.path !== filePath)
        .map(node => {
          if (node.children) {
            return { ...node, children: removeNode(node.children) };
          }
          return node;
        });
    };

    onFilesChange(removeNode(files));
    handleTabClose(filePath);
  };

  const handleFileRename = (filePath: string, newName: string) => {
    const renameNode = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === filePath) {
          const parentPath = filePath.substring(0, filePath.lastIndexOf('/'));
          const newPath = parentPath ? `${parentPath}/${newName}` : `/${newName}`;
          return {
            ...node,
            name: newName,
            path: newPath,
            language: node.type === 'file' ? getLanguageFromFileName(newName) : undefined
          };
        }
        if (node.children) {
          return { ...node, children: renameNode(node.children) };
        }
        return node;
      });
    };

    onFilesChange(renameNode(files));
  };

  const getLanguageFromFileName = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py': return 'python';
      case 'js': return 'javascript';
      case 'ts': return 'typescript';
      case 'json': return 'json';
      case 'yml':
      case 'yaml': return 'yaml';
      case 'md': return 'markdown';
      case 'txt': return 'text';
      default: return 'text';
    }
  };

  const getCurrentFile = () => {
    return openFiles.find(f => f.path === activeFile);
  };

  const currentFile = getCurrentFile();

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden" style={{ height }}>
      <div className="bg-gray-900 flex" style={{ height: '100%' }}>
        {/* File Explorer */}
        <div className="w-64 border-r border-gray-700">
          <FileExplorer
            files={files}
            selectedFile={activeFile}
            onFileSelect={handleFileSelect}
            onFileCreate={handleFileCreate}
            onFileDelete={handleFileDelete}
            onFileRename={handleFileRename}
          />
        </div>

        {/* Editor Area */}
        <div className="flex-1 flex flex-col">
          {/* Tabs */}
          <EditorTabs
            openFiles={openFiles}
            activeFile={activeFile}
            onTabSelect={handleFileSelect}
            onTabClose={handleTabClose}
          />

          {/* Editor */}
          {currentFile ? (
            <div className="flex-1">
              <Editor
                height="100%"
                language={currentFile.language || 'text'}
                value={fileContents[currentFile.path] || ''}
                onChange={(value) => updateFileContent(currentFile.path, value || '')}
                theme={theme}
                options={{
                  minimap: { enabled: true },
                  fontSize: 14,
                  fontFamily: "'Fira Code', 'Cascadia Code', 'Monaco', monospace",
                  lineNumbers: 'on',
                  rulers: [80, 120],
                  wordWrap: 'on',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  tabSize: 4,
                  insertSpaces: true,
                  cursorStyle: 'line',
                  smoothScrolling: true,
                  contextmenu: true,
                  folding: true,
                  foldingStrategy: 'indentation',
                  showFoldingControls: 'mouseover',
                  bracketPairColorization: {
                    enabled: true
                  },
                  guides: {
                    indentation: true,
                    bracketPairs: true
                  },
                  quickSuggestions: {
                    other: true,
                    comments: true,
                    strings: true
                  },
                  suggestOnTriggerCharacters: true,
                  parameterHints: {
                    enabled: true
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-900 text-gray-500">
              <div className="text-center">
                <i className="fas fa-code text-6xl mb-4"></i>
                <p>Select a file to edit</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VSCodeEditor;